'use client'

import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { Search } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { AgentType } from 'sprites-types/api/v2/copilot'
import { GoogleIcon, MetaIcon } from '../icons/PlatformIcons'

interface AgentModeSelectorProps {
  selectedMode: AgentType
  onModeChange: (mode: AgentType) => void
}

const AGENT_MODES = [
  {
    type: AgentType.META_ADS,
    label: 'Meta Ads',
    icon: MetaIcon,
    gradient: 'from-orange-400 to-pink-400',
    bgColor: 'bg-gray-100',
  },
  {
    type: AgentType.GOOGLE_ADS,
    label: 'Google Ads',
    icon: GoogleIcon,
    gradient: 'from-emerald-500 to-green-600',
    bgColor: 'bg-gradient-to-r from-[#F2FDF8] to-[#FAFFED]',
  },
  {
    type: AgentType.SEO,
    label: 'SEO',
    icon: Search,
    gradient: 'from-cyan-400 to-blue-500',
    bgColor: 'bg-gray-100',
  },
] as const

export const AgentModeSelector = ({
  selectedMode,
  onModeChange,
}: AgentModeSelectorProps): ReactElement => {
  const [hoveredMode, setHoveredMode] = useState<AgentType | null>(null)
  const selectedIndex = AGENT_MODES.findIndex((mode) => mode.type === selectedMode)
  const numModes = AGENT_MODES.length

  return (
    <div className="mb-6 flex justify-center">
      <motion.div
        className="relative inline-grid rounded-2xl border border-gray-200/80 bg-white/70 p-1 shadow-lg backdrop-blur-sm"
        style={{
          gridTemplateColumns: `repeat(${numModes}, 1fr)`,
          gap: '3px',
        }}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        {/* Animated background indicator */}
        <motion.div
          className={cn(
            'absolute rounded-2xl',
            AGENT_MODES[selectedIndex].gradient,
            'bg-gradient-to-r'
          )}
          style={{
            top: '4px',
            bottom: '4px',
            width: `calc((100% - ${(numModes - 1) * 3}px - 8px) / ${numModes})`,
          }}
          initial={false}
          animate={{
            left: `calc(4px + ${selectedIndex} * ((100% - ${(numModes - 1) * 3}px - 8px) / ${numModes} + 3px))`,
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30,
          }}
        />

        {AGENT_MODES.map(({ type, label, icon: Icon, bgColor }) => {
          const isSelected = selectedMode === type
          const isHovered = hoveredMode === type

          return (
            <motion.button
              key={type}
              onClick={() => onModeChange(type)}
              onHoverStart={() => setHoveredMode(type)}
              onHoverEnd={() => setHoveredMode(null)}
              className={cn(
                'relative z-10 flex items-center justify-center gap-1.5 rounded-2xl px-4 py-2 text-sm font-medium transition-all duration-200',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2',
                isSelected ? 'text-white' : 'text-gray-700 hover:text-gray-900'
              )}
              whileTap={{ scale: 0.97 }}
            >
              {/* Hover effect background */}
              <AnimatePresence>
                {isHovered && !isSelected && (
                  <motion.div
                    className={cn('absolute inset-0 rounded-2xl', bgColor)}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                  />
                )}
              </AnimatePresence>

              <Icon className="relative z-10 h-3.5 w-3.5" />
              <span className="font-space-grotesk relative z-10">{label}</span>
            </motion.button>
          )
        })}
      </motion.div>
    </div>
  )
}
